import * as React from 'react';
import { DataOwnerRequestContext } from '../../../Common/DataOwnerModels/DataOwnerRequestDetails';
import {
  canGoToNextStep,
  canGoToPreviousStep,
  CoherenceWizard,
  goToNextStep,
  goToPreviousStep,
  goToStepById,
  ISubwayNavNodeProps,
  IWizardProps,
  IWizardStepButtonProps,
  IWizardStepProps,
  SubwayNavNodeState,
  WizardActionBar,
  WizardType,
} from '@coherence-design-system/controls';
import { v4 as uuidv4 } from 'uuid';
import {
  Checkbox,
  ICommandBarItemProps,
  Label,
  Spinner,
  Icon,
  IconButton,
  Dropdown,
  IDropdownOption,
  PrimaryButton,
  TextField,
} from '@fluentui/react';
import { PageHeading } from '../../../Components/PageHeading';
import * as styled from './DataOwnerManagement.styled';
import { CancelButtonStyles } from '../../../Common/Styles/common.styled';
import { useContext, useEffect, useState } from 'react';
import { RequestStep } from './RequestSteps/RequestStep';
import { DependentEntity, Rule, RuleSetRecord, RuleSetTypeSingle } from '../../../Common/DataOwnerModels/RuleSetRecord';
import { CdmStep } from './RequestSteps/CdmStep/CdmStep';
import { ReviewStep } from './RequestSteps/ReviewStep';
import { RuleSetRequestPanel } from './RequestSteps/RuleSetStep/RuleSetManagement';
import { CDMRecord } from '../../../Common/DataOwnerModels/CDMRecord';

export interface IPanelWizardState {
  showPanel: boolean;
  wizardProps: IWizardProps;
  content: JSX.Element;
}

export type AddDataOwnerRequestPanelProps = {
  isOpen?: boolean;
  dismissPanel(): void;
  setIsSaveDataOwnerRequest(val: boolean): void;
  setIsSubmitDataOwnerRequest(val: boolean): void;
  setShowSuccessMessage(val: boolean): void;
  setShowErrorMessage(val: boolean): void;
  onSubmitErrorFlg: boolean;
  setOnSubmitErrorFlg(val: boolean): void;
};

export function AddDataOwnerRequestPanel(props: AddDataOwnerRequestPanelProps): React.ReactElement {
  const { dataOwnerRequestsList, dataOwnerRequestDetails } = useContext(DataOwnerRequestContext);

  const environment: number = dataOwnerRequestDetails.request.environment;
  const nonProdApprovedCdmList: CDMRecord[] = dataOwnerRequestDetails.nonProdApprovedCdms;
  const prodApprovedCdmList: CDMRecord[] = dataOwnerRequestDetails.prodApprovedCdms;
  const cdmList: CDMRecord[] = dataOwnerRequestDetails.cdms;

  // Form State
  const [isApprovedRuleSetListLoading, setIsApprovedRuleSetListLoading] = useState<boolean>(false);
  const [cdmDropdownOptionList, setCdmDropdownOptionList] = useState<IDropdownOption[]>([]);
  const [isApprovedCdmListLoading, setIsApprovedCdmListLoading] = useState<boolean>(true);

  // Local variables
  const existingCDMList = nonProdApprovedCdmList.concat(prodApprovedCdmList.concat(dataOwnerRequestDetails.cdms));

  useEffect(() => {
    setIsApprovedCdmListLoading(true);
    let newApprovedCdmList: CDMRecord[] = [];

    // Create list of dropdown options for approved CDM list
    if (environment == 0 && dataOwnerRequestDetails.nonProdApprovedCdms != null) {
      newApprovedCdmList = JSON.parse(JSON.stringify(dataOwnerRequestDetails.nonProdApprovedCdms)); // deep copy
    } else if (environment == 1 && dataOwnerRequestDetails.prodApprovedCdms != null) {
      newApprovedCdmList = JSON.parse(JSON.stringify(dataOwnerRequestDetails.prodApprovedCdms)); // deep copy
    }
    const newCdmDropdownOptions: IDropdownOption[] = [];

    // Adding existing Approved CDMs
    for (const cdmRecord of newApprovedCdmList) {
      newCdmDropdownOptions.push({
        key: cdmRecord.id,
        text: cdmRecord.name,
      });
    }

    // Adding newly added CDMs from context
    for (const cdmRecord of cdmList) {
      newCdmDropdownOptions.push({
        key: cdmRecord.id,
        text: cdmRecord.name,
      });
    }
    setCdmDropdownOptionList(newCdmDropdownOptions);
    setIsApprovedCdmListLoading(false);
  }, [
    dataOwnerRequestDetails.cdms,
    environment,
    dataOwnerRequestDetails.nonProdApprovedCdms,
    dataOwnerRequestDetails.prodApprovedCdms,
  ]);

  const _getDataOwnerWizardSteps = (): IWizardStepProps[] => {
    const firstFooter = (
      <WizardActionBar
        isLoading={false}
        mainAction={_goToNextStepAction('Next')}
        savecloseAction={_saveCloseAction()}
        cancelAction={_goToCancelStepAction()}
        currentStep={{} as IWizardStepProps}
      />
    );
    const inBetweenFooter = (
      <WizardActionBar
        isLoading={false}
        backAction={_goToPrevStepAction()}
        savecloseAction={_saveCloseAction()}
        mainAction={_goToNextStepAction('Next')}
        cancelAction={_goToCancelStepAction()}
        currentStep={{} as IWizardStepProps}
      />
    );
    const lastFooter = (
      <WizardActionBar
        backAction={_goToPrevStepAction()}
        savecloseAction={_saveCloseAction()}
        mainAction={_goToSubmittedAction(props.onSubmitErrorFlg)}
        cancelAction={_goToCancelStepAction()}
        currentStep={{} as IWizardStepProps}
      />
    );

    function addAndGetNewRuleSetId(newRuleSetId: string): string {
      const currentRuleSet: RuleSetRecord = {
        id: newRuleSetId,
        tenantId: dataOwnerRequestDetails.dataOwnerId,
        name: 'New Rule Set ',
        status: 'Draft',
        version: 1.0,
        ruleSetType: RuleSetTypeSingle,
        category: null,
        dataSource: null,
        filterExpression: null,
        rules: [],
      };
      dataOwnerRequestDetails.addRuleSetList(currentRuleSet);
      return currentRuleSet.id;
    }

    const commandBarAddRuleSetItems: ICommandBarItemProps = {
      key: 'addRuleSet',
      text: 'Add Rule Set',
      iconProps: { iconName: 'Add' },
      onClick: () => {
        const ruleSetId = uuidv4();
        const newSubStep: IWizardStepProps = {
          id: ruleSetId,
          label: 'New Rule Set ',
          onClickStep: _handleClickStep,
          state: SubwayNavNodeState.NotStarted,
          footerElement: inBetweenFooter,
          announcedProps: { message: 'Rule Set details' },
          wizardContent: {
            contentTitleElement: _getContentTitleElement('Rule Set Details'),
            content: (
              <RuleSetRequestPanel
                ruleSetId={addAndGetNewRuleSetId(ruleSetId)}
                cdmDropdownOptionList={cdmDropdownOptionList}
                isApprovedCdmListLoading={isApprovedCdmListLoading}
                updateRuleSetStepLabel={updateRuleSetStepLabel}
              />
            ),
          },
        };
        let tempRuleSetSteps: IWizardStepProps[] = panelWizardState.wizardProps.steps[2].subSteps;
        if (tempRuleSetSteps == null) {
          tempRuleSetSteps = [{ ...ruleSetsListSubStep }];
        }
        tempRuleSetSteps.push(newSubStep);
        requestSteps[2].subSteps = tempRuleSetSteps;

        setRuleSetSteps([...ruleSetSteps]);
        setRequestSteps([...requestSteps]);

        const computedSteps = goToStepById(requestSteps, newSubStep.id, SubwayNavNodeState.Unsaved).steps;
        panelWizardState.wizardProps.steps = computedSteps;
        setPanelWizardState({ ...panelWizardState });
      },
      buttonStyles: {
        root: {
          margin: '0px 0px 0px 0px',
        },
      },
    };

    // RuleSet List Page
    function RuleSetListComponent(): React.ReactElement {
      const {dataOwnerRequestDetails} = useContext(DataOwnerRequestContext);
      // Form State
      const [ruleSetIdCheckedList, setRuleSetIdCheckedList] = useState<string[]>([]);
      const [isRemoveRuleSetsButtonDisabled, setIsRemoveRuleSetsButtonDisabled] = useState<boolean>(true);
      const [isAddExistingRuleSetFormVisible, setIsAddExistingRuleSetFormVisible] = useState<boolean>(false);
      const [approvedRuleSetList, setApprovedRuleSetList] = useState<RuleSetRecord[]>([]);
      const [rulesetDropdownOptionList, setRuleSetDropdownOptionList] = useState<IDropdownOption[]>([]);
      const [selectedExistingRuleset, setSelectedExistingRuleset] = useState<RuleSetRecord>(null);

      const resetAddExistingRuleSetForm = () => {
        setSelectedExistingRuleset(null);
      };

      const handleClickAddExistingRuleSet = () => {
        const existingRuleSet: RuleSetRecord = { ...selectedExistingRuleset };
        /*
        // Update Version changes
        const updatedVersionIncrement: number = existingRuleSet.version + 0.1;
        const updatedVersionRounded: number = Math.round(updatedVersionIncrement * 10) / 10;
        */
        dataOwnerRequestDetails.addRuleSetList(existingRuleSet);

        resetAddExistingRuleSetForm();
        setIsAddExistingRuleSetFormVisible(true);
      };

      const commandBarAddExistingRuleSetItems: ICommandBarItemProps = {
        key: 'addExistingRuleSet',
        text: 'Add Existing Rule Set',
        iconProps: { iconName: 'Add' },
        disabled: !isApprovedRuleSetListLoading && approvedRuleSetList.length <= 0,
        onClick: () => {
          resetAddExistingRuleSetForm();
          setIsAddExistingRuleSetFormVisible(true);
        },
        buttonStyles: {
          root: {
            margin: '0px 0px 0px 0px',
          },
        },
      };

      const onChangeSelectExistingRuleSet = (event: React.FormEvent<HTMLDivElement>, item: IDropdownOption): void => {
        if (item) {
          const ruleSetId = item.key as string;
          const ruleSetRecord: RuleSetRecord = approvedRuleSetList.find((x) => x.id === ruleSetId);
          setSelectedExistingRuleset(ruleSetRecord);
        }
      };

      // Loading Existing Approved RuleSets
      useEffect(() => {
        setIsApprovedRuleSetListLoading(true);
        let newApprovedList: RuleSetRecord[] = [];

        // Create list of dropdown options for approved CDM list
        if (dataOwnerRequestDetails?.request?.environment == 0) {
          newApprovedList = JSON.parse(JSON.stringify(dataOwnerRequestDetails?.nonProdApprovedRuleSets)); // deep copy
        } else {
          newApprovedList = JSON.parse(JSON.stringify(dataOwnerRequestDetails?.prodApprovedRuleSets)); // deep copy
        }
        const newDropdownOptions: IDropdownOption[] = [];
        for (const record of newApprovedList) {
          newDropdownOptions.push({
            key: record.id,
            text: record.name,
          });
        }
        setApprovedRuleSetList(newApprovedList);
        setRuleSetDropdownOptionList(newDropdownOptions);
        setIsApprovedRuleSetListLoading(false);
      }, [dataOwnerRequestDetails?.nonProdApprovedRuleSets, dataOwnerRequestDetails?.prodApprovedRuleSets]);

      const commandBarDeleteRuleSetItems: ICommandBarItemProps[] = [
        {
          key: 'removeRuleSet',
          text: 'Remove Rule Set',
          iconProps: { iconName: 'Delete' },
          disabled: isRemoveRuleSetsButtonDisabled,
          onClick: () => {
            // Delete Rulesets from Context object
            dataOwnerRequestDetails.deleteRuleSets(ruleSetIdCheckedList);

            // Deletion list: Only Saved RuleSets in the storage needs to be deleted by API.  So, ignoring the RuleSets that have been added in the Requested & deleted before Saving.
            const savedRuleSetDeletionList = ruleSetIdCheckedList.filter((x) =>
              dataOwnerRequestDetails.savedRuleSetIdList.includes(x)
            );
            dataOwnerRequestDetails.updateDeleteRuleSetList(
              dataOwnerRequestDetails.ruleSetDeletionList.concat(savedRuleSetDeletionList)
            );

            setRuleSetIdCheckedList([]);
            setIsRemoveRuleSetsButtonDisabled(true);
          },
          buttonStyles: {
            root: {
              margin: '0px 0px 0px 16px',
            },
          },
        },
      ];

      const renderRuleSetTitleDropdown = (ruleSetObj: RuleSetRecord, ruleSetIndex: number): JSX.Element => {
        const isChecked = ruleSetIdCheckedList.includes(ruleSetObj.id);
        const navigateToRuleSet = () => {
          const computedSteps = goToStepById(
            requestSteps,
            ruleSetObj.id, //subStepId
            SubwayNavNodeState.Unsaved
          ).steps;
          panelWizardState.wizardProps.steps = computedSteps;
          setPanelWizardState({ ...panelWizardState });
        };
        return (
          <styled.SingleRuleContainer>
            <styled.CheckTextIconContainer>
              <styled.CheckTextContainer>
                <Checkbox
                  key={'CheckBox' + ruleSetObj.id}
                  id={'CheckBox' + ruleSetObj.id}
                  ariaLabel={`${ruleSetObj.name} Check Box`}
                  checked={isChecked}
                  onChange={(ev, checked): void => _onChangeRuleSetSelectCheckBox(checked, ruleSetObj, ruleSetIndex)}
                  styles={styled.checkBoxStyles}
                />
                <styled.NavigationTextStyled
                  onClick={() => navigateToRuleSet()}
                  title="Click to navigate to the respective RuleSet"
                >
                  {ruleSetObj.name}
                </styled.NavigationTextStyled>
              </styled.CheckTextContainer>
              <Icon
                data-testid="goto-ruleSet"
                aria-label="Goto RuleSet"
                iconName="ChevronRight"
                className={styled.dropdownIconClass}
                onClick={() => navigateToRuleSet()}
              />
            </styled.CheckTextIconContainer>
            <styled.LineDivider />
          </styled.SingleRuleContainer>
        );
      };

      const _onChangeRuleSetSelectCheckBox = (checked: boolean, ruleSetObj: RuleSetRecord, ruleSetIndex: number) => {
        const newRuleSetIdCheckedList: string[] = JSON.parse(
          JSON.stringify(ruleSetIdCheckedList) // deep copy
        );
        if (checked) {
          newRuleSetIdCheckedList.push(ruleSetObj.id);
        } else {
          newRuleSetIdCheckedList.splice(ruleSetIndex, 1);
        }

        if (newRuleSetIdCheckedList.length > 0) {
          setIsRemoveRuleSetsButtonDisabled(false);
        } else {
          setIsRemoveRuleSetsButtonDisabled(true);
        }
        setRuleSetIdCheckedList(newRuleSetIdCheckedList);
      };

      return (
        <>
          <styled.StyledCommandBar
            items={[commandBarAddRuleSetItems].concat(
              commandBarDeleteRuleSetItems.concat(commandBarAddExistingRuleSetItems)
            )}
            ariaLabel="Command Bar"
          />

          <styled.RuleListContainer>
            {dataOwnerRequestDetails?.ruleSets?.map((ruleSetObj: RuleSetRecord, ruleSetIndex: number) => (
              <div key={ruleSetObj.id}>{renderRuleSetTitleDropdown(ruleSetObj, ruleSetIndex)}</div>
            ))}
          </styled.RuleListContainer>

          {isAddExistingRuleSetFormVisible && (
            <styled.AddExistingObjectContainer>
              <styled.AddExistingObjectTitleContainer>
                <styled.AddExistingObjectTitleTextStyled>Add existing Ruleset</styled.AddExistingObjectTitleTextStyled>
                <IconButton
                  data-testid="add-existing-ruleset-form-cancel"
                  id="AddExistingRuleSetCancelButton"
                  iconProps={{ iconName: 'Cancel' }}
                  ariaLabel="Cancel Add Existing RuleSet Button"
                  onClick={() => {
                    resetAddExistingRuleSetForm();
                    setIsAddExistingRuleSetFormVisible(false);
                  }}
                  styles={CancelButtonStyles}
                />
              </styled.AddExistingObjectTitleContainer>

              <Dropdown
                data-testid="add-existing-ruleset-form-select"
                id="selectExistingRuleset"
                placeholder="Select Existing Ruleset"
                ariaLabel="Select Existing Ruleset"
                selectedKey={selectedExistingRuleset?.id}
                onChange={onChangeSelectExistingRuleSet}
                options={rulesetDropdownOptionList}
                disabled={isApprovedRuleSetListLoading}
              />
              {selectedExistingRuleset != null && (
                <>
                  <TextField
                    label="Description"
                    id="description"
                    placeholder="Entered custom description"
                    value={selectedExistingRuleset?.description}
                    required={false}
                    validateOnLoad={false}
                    validateOnFocusOut={true}
                    multiline={true}
                    autoAdjustHeight={true}
                    disabled={true}
                  />
                  <Dropdown
                    id="sourceCdm"
                    label="Source CDM"
                    placeholder="No Source CDM selected"
                    ariaLabel="Source CDM"
                    selectedKey={selectedExistingRuleset?.dataSource?.entityTypeId}
                    options={cdmDropdownOptionList}
                    multiSelect={false}
                    disabled={true}
                  />
                  <TextField
                    label="Dependent CDMs"
                    id="dependentCdms"
                    placeholder="No Dependent CDMs selected"
                    value={selectedExistingRuleset?.dataSource?.dependentEntities
                      ?.map(
                        (depCdm: DependentEntity) => existingCDMList?.find((x) => x.id == depCdm?.entityTypeId)?.name
                      )
                      .join('; ')}
                    required={false}
                    validateOnLoad={false}
                    validateOnFocusOut={true}
                    multiline={true}
                    autoAdjustHeight={true}
                    disabled={true}
                  />
                  <TextField
                    label="Rules"
                    id="rules"
                    placeholder="No Rules are configured"
                    value={selectedExistingRuleset?.rules?.map((ruleObj: Rule) => ruleObj?.ruleName).join('; \n')}
                    required={false}
                    validateOnLoad={false}
                    validateOnFocusOut={true}
                    multiline={true}
                    autoAdjustHeight={true}
                    disabled={true}
                  />
                </>
              )}
              <PrimaryButton
                text="Add RuleSet"
                disabled={selectedExistingRuleset == null}
                allowDisabledFocus={true}
                onClick={handleClickAddExistingRuleSet}
              />
            </styled.AddExistingObjectContainer>
          )}
        </>
      );
    }

    const ruleSetsListSubStep: IWizardStepProps = {
      id: 'subStepRuleSetsList',
      label: 'Rule Set List',
      onClickStep: _handleClickStep,
      state: SubwayNavNodeState.NotStarted,
      footerElement: inBetweenFooter,
      announcedProps: { message: 'List of Rule Sets' },
      wizardContent: {
        contentTitleElement: _getContentTitleElement('List of Rule Sets'),
        content: <RuleSetListComponent />,
      },
    };

    const [ruleSetSteps, setRuleSetSteps] = useState<IWizardStepProps[]>(computeSubStepsinEdit());
    const initialRuleSetSteps: IWizardStepProps[] =
      dataOwnerRequestDetails?.ruleSets?.length <= 0 ? null : computeSubStepsinEdit();

    useEffect(() => {
      let currentStepId = null;
      const currentStageSteps = panelWizardState.wizardProps.steps;
      if (currentStageSteps[2].state == SubwayNavNodeState.CurrentWithSubSteps) {
        currentStageSteps[2].subSteps.forEach((x) => {
          if (x.state == SubwayNavNodeState.Current) {
            currentStepId = x.id;
          }
        });
      } else {
        currentStageSteps.forEach((x) => {
          if (x.state == SubwayNavNodeState.Current) {
            currentStepId = x.id;
          }
        });
      }
      const tempRuleSetSteps = computeSubStepsinEdit();
      requestSteps[2].subSteps = tempRuleSetSteps;
      setRuleSetSteps([...tempRuleSetSteps]);
      setRequestSteps([...requestSteps]);

      const computedSteps = goToStepById(requestSteps, currentStepId, SubwayNavNodeState.Unsaved).steps;
      panelWizardState.wizardProps.steps = computedSteps;
      setPanelWizardState({ ...panelWizardState });
    }, [dataOwnerRequestDetails?.ruleSets]);

    function computeSubStepsinEdit(): IWizardStepProps[] {
      const initialSubSteps: IWizardStepProps[] = [{ ...ruleSetsListSubStep }];
      dataOwnerRequestDetails?.ruleSets?.map((item) => {
        const newSubStep: IWizardStepProps = {
          id: item.id, // subStepId
          label: item.name,
          onClickStep: _handleClickStep,
          state: SubwayNavNodeState.NotStarted,
          footerElement: inBetweenFooter,
          announcedProps: { message: 'Rule Set details' },
          wizardContent: {
            contentTitleElement: _getContentTitleElement('Rule Set Details'),
            content: (
              <RuleSetRequestPanel
                ruleSetId={item.id}
                cdmDropdownOptionList={cdmDropdownOptionList}
                isApprovedCdmListLoading={isApprovedCdmListLoading}
                updateRuleSetStepLabel={updateRuleSetStepLabel}
              />
            ),
          },
        };
        initialSubSteps.push(newSubStep);
      });

      return initialSubSteps;
    }

    const initialRequestSteps = [
      {
        id: 'step1',
        label: 'Request Details',
        onClickStep: _handleClickStep,
        state: SubwayNavNodeState.Current,
        footerElement: firstFooter,
        announcedProps: { message: 'Request Details' },
        wizardContent: {
          contentTitleElement: _getContentTitleElement('Request Details'),
          content: <RequestStep />,
        },
      },
      {
        id: 'step2',
        label: 'Entity Details',
        onClickStep: _handleClickStep,
        state: SubwayNavNodeState.NotStarted,
        footerElement: inBetweenFooter,
        wizardContent: {
          contentTitleElement: _getContentTitleElement('Entities'),
          content: <CdmStep />,
        },
      },
      {
        id: 'step3',
        label: 'Rule Set Details',
        onClickStep: _handleClickStep,
        state: SubwayNavNodeState.NotStarted,
        announcedProps: { message: 'RuleSets' },
        footerElement: inBetweenFooter,
        subSteps: initialRuleSetSteps,
        wizardContent: {
          contentTitleElement: _getContentTitleElement('RuleSets'),
          content: (
            <styled.InitialWizardStepContainer>
              <styled.InitialWizardStepTextStyled>
                Add a new or existing Ruleset to get started...
              </styled.InitialWizardStepTextStyled>
              <styled.InitialWizardStepCommandBar
                items={[commandBarAddRuleSetItems]} //.concat(commandBarAddExistingRuleSetItems)}
                ariaLabel="Initial Ruleset Command Bar"
              />
            </styled.InitialWizardStepContainer>
          ),
        },
      },
      {
        id: 'step4',
        label: 'Review & Submit',
        onClickStep: _handleClickStep,
        state: SubwayNavNodeState.NotStarted,
        announcedProps: { message: 'Success' },
        footerElement: lastFooter,
        wizardContent: {
          content: (
            <ReviewStep onSubmitErrorFlg={props.onSubmitErrorFlg} setOnSubmitErrorFlg={props.setOnSubmitErrorFlg} />
          ),
        },
      },
    ];

    const updateRuleSetStepLabel = (ruleSetId: string, ruleSetNewName: string) => {
      const currentRequestSteps = panelWizardState.wizardProps.steps;
      const currentRuleSetSubSteps = currentRequestSteps[2].subSteps;
      currentRuleSetSubSteps.forEach((subStep) => {
        if (subStep.id == ruleSetId) {
          subStep.label = ruleSetNewName;
        }
      });
      setRuleSetSteps(currentRuleSetSubSteps);
      setRequestSteps(currentRequestSteps);
      const computedSteps = goToStepById(currentRequestSteps, ruleSetId, null).steps;
      panelWizardState.wizardProps.steps = computedSteps;
      setPanelWizardState({ ...panelWizardState });
    };

    const [requestSteps, setRequestSteps] = useState<IWizardStepProps[]>(initialRequestSteps);
    return requestSteps;
  };

  const _getCompletedStep = (): IWizardStepProps => {
    return {
      id: 'Complete wizard',
      label: 'Complete wizard',
      state: SubwayNavNodeState.WizardComplete,
      announcedProps: {
        message: 'The request is submitted successfully!',
      },
      wizardContent: {
        content: <h2>The request is submitted successfully!</h2>,
      },
      footerElement: null,
    };
  };

  const initStateOfPanelWizard: IPanelWizardState = {
    showPanel: false,
    wizardProps: {
      steps: _getDataOwnerWizardSteps(),
      wizardComplete: false,
      wizardCompleteStep: _getCompletedStep(),
      isLoading: false,
    },
    content: null,
  };
  const [panelWizardState, setPanelWizardState] = React.useState(initStateOfPanelWizard);

  function _saveCloseAction(): IWizardStepButtonProps {
    return {
      text: 'Save',
      onClick: async () => {
        props.setIsSaveDataOwnerRequest(true);
      },
    };
  }

  function _goToNextStepAction(buttonLabel: string): IWizardStepButtonProps {
    return {
      text: buttonLabel,
      onClick: () => {
        console.log(
          'At next button' + dataOwnerRequestDetails?.request?.id + 'name' + dataOwnerRequestDetails?.request?.name
        );
        if (canGoToNextStep(panelWizardState.wizardProps.steps)) {
          const { steps, newCurrentStep } = goToNextStep(
            panelWizardState.wizardProps.steps,
            SubwayNavNodeState.ViewedNotCompleted
          );

          if (steps) {
            // Correcting if the current step is not reset to "Unsaved" while moving to Next step. This is functionality of goToNextStep() but with dynamic subsets its not restting current subsets sometimes
            steps[2]?.subSteps
              ?.filter(
                (x) =>
                  x.id != newCurrentStep.id && x.state != newCurrentStep.state && x.state == SubwayNavNodeState.Current
              )
              .forEach((x) => (x.state = SubwayNavNodeState.Unsaved));
            if (steps[2]?.subSteps?.filter((x) => x.id == newCurrentStep.id).length > 0) {
              steps[2].subSteps.filter((x) => x.id == newCurrentStep.id)[0].state = SubwayNavNodeState.Current;
            }

            panelWizardState.wizardProps.steps = steps;
            setPanelWizardState({ ...panelWizardState });
          } else {
            console.log('Oops, something happened when trying to go to the next steps');
          }
        }
      },
    };
  }

  function _goToSubmittedAction(isDisableSubmit: boolean): IWizardStepButtonProps {
    return {
      text: 'Submit',
      onClick: () => {
        if (!isDisableSubmit) {
          props.setIsSubmitDataOwnerRequest(true);
        }
      },
      disabled: isDisableSubmit,
    };
  }

  function _goToCancelStepAction(): IWizardStepButtonProps {
    return {
      text: 'Cancel',
      onClick: () => {
        props.dismissPanel();
      },
    };
  }

  function _goToPrevStepAction(): IWizardStepButtonProps {
    return {
      text: 'Back',
      onClick: () => {
        if (canGoToPreviousStep(panelWizardState.wizardProps.steps)) {
          const { steps, newCurrentStep } = goToPreviousStep(
            panelWizardState.wizardProps.steps,
            SubwayNavNodeState.ViewedNotCompleted
          );

          if (steps) {
            panelWizardState.wizardProps.steps = steps;
            setPanelWizardState({ ...panelWizardState });
          } else {
            console.log('Oops, something happened when trying to go to the next steps');
          }
        }
      },
    };
  }

  function _handleClickStep(step: ISubwayNavNodeProps): void {
    const computedSteps = goToStepById(panelWizardState.wizardProps.steps, step.id, SubwayNavNodeState.Unsaved).steps;
    panelWizardState.wizardProps.steps = computedSteps;
    setPanelWizardState({ ...panelWizardState });
  }

  function _getContentTitleElement(stepStr: string): JSX.Element {
    return <span>{stepStr}</span>;
  }

  return (
    <>
      <PageHeading>Data Owner Onboarding</PageHeading>
      <div>
        {isApprovedRuleSetListLoading && (
          <>
            <Spinner label="" />
            <Label style={{ fontSize: 15 }}>Loading your content...</Label>
          </>
        )}
        {!isApprovedRuleSetListLoading && (
          <CoherenceWizard
            type={WizardType.PANEL}
            wizardProps={{
              ...panelWizardState.wizardProps,
            }}
            panelProps={{
              isOpen: props.isOpen,
              onDismiss: () => props.dismissPanel(),
              headerText: ' Request',
              popupProps: {
                ariaLabel: ' Request',
                ariaLabelledBy: '',
              },
              layerProps: { eventBubblingEnabled: true },
            }}
          />
        )}
      </div>
    </>
  );
}
